import { type Channel, type Chat } from '@pubnub/chat';
import { useEffect } from 'react';
import { useConversationsStore } from '@avanta/shared/stores/chat/conversations';

interface UsePresenceSubscriptionParams {
  activeChannelId: string | null;
  chat: Chat | null;
  otherUserId: string | undefined;
}

// The SDK's push-based streamPresence callback isn't reliably delivered while
// the conversation stays mounted (join/leave/timeout events can go missing),
// so this polls whoIsPresent as a fallback. Kept short so a status change
// still feels close to real time without leaving and re-entering the chat.
const PRESENCE_POLL_INTERVAL_MS = 10000;

export const usePresenceSubscription = ({
  activeChannelId,
  chat,
  otherUserId,
}: UsePresenceSubscriptionParams): void => {
  const setIsOtherUserOnline = useConversationsStore(state => state.setIsOtherUserOnline);

  useEffect(() => {
    if (!activeChannelId || !chat || !otherUserId) return;
    let cancelled = false;
    let unsub: (() => void) | undefined;
    let intervalId: ReturnType<typeof setInterval> | undefined;

    const refreshPresence = async (channel: Channel): Promise<void> => {
      const presentUserIds = await channel.whoIsPresent();
      if (cancelled) return;
      setIsOtherUserOnline(presentUserIds.includes(otherUserId));
    };

    const setup = async (): Promise<void> => {
      const channel = await chat.getChannel(activeChannelId);
      if (!channel || cancelled) return;

      await refreshPresence(channel);
      if (cancelled) return;

      intervalId = setInterval(() => void refreshPresence(channel), PRESENCE_POLL_INTERVAL_MS);

      unsub = await channel.streamPresence((userIds: string[]) => {
        if (cancelled) return;
        setIsOtherUserOnline(userIds.includes(otherUserId));
      });
    };

    setup();

    return (): void => {
      cancelled = true;
      if (intervalId) clearInterval(intervalId);
      unsub?.();
      setIsOtherUserOnline(false);
    };
  }, [activeChannelId, chat, otherUserId, setIsOtherUserOnline]);
};
